import * as React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CompassNameIcon from "../images/general/CgiNameIconBlack.svg";
import CompassLogoGradient from "../images/general/CompassIconGradient.svg";
import SpecialText from "../components/SpecialText";
import LinearProgressWithLabel from "../components/LinearProgressWIthLabel";
import CircularProgressWithLabel from "../components/CircularProgressWithLabel";
import { useTranslation } from "react-i18next";
import {
  CGI_GREY,
  CGI_RED,
  CGI_WARNING,
  returnColor,
  discreteDescriptions,
} from "../helpers/enums";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from "recharts";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Icon,
  Typography,
} from "@mui/material";

export default function PublicAssessmentResults({
  evaluationData,
  exit,
  showExit = true,
}) {
  const { t } = useTranslation();
  if (!evaluationData)
    return (
      <Typography sx={{ ml: 3 }}>{t("assessmentNotFullyCompleted")}</Typography>
    );
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 1,
      }}
    >
      {showExit && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: { xs: "auto", md: 1 },
            mt: { xs: 7, md: -5 },
          }}
        >
          <Icon sx={{ width: "auto", height: "auto" }}>
            <img draggable={false} src={CompassLogoGradient} height={"55px"} />
          </Icon>
          <Icon sx={{ width: "auto", height: "auto", ml: 2 }}>
            <img draggable={false} src={CompassNameIcon} height="50px" />
          </Icon>
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 35,
          fontWeight: 400,
          ml: 2,
          my: 4,
        }}
      >
        {t("evaluationOfAssessment")}{" "}
        <SpecialText
          style={{
            fontSize: 37,
            fontStyle: "italic",
            pr: 1,
            fontWeight: 500,
            background:
              "-webkit-linear-gradient(45deg, #E31937, #A82465 60%, #5236AB)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          "{evaluationData?.title}"
        </SpecialText>
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: 1 / 2, lg: 1 / 3 },
            justifyContent: "center",
          }}
        >
          <Box
            id="dataByCategoryProgressId"
            sx={{
              ml: { xs: 1, lg: 3 },
              display: "flex",
              flexDirection: "column",
              mr: 2,
            }}
          >
            {Object.values(evaluationData.assessmentModules).map(
              (moduleData, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    pb: 1,
                    borderBottom: 1,
                    borderTop: index === 0 ? 1 : 0,
                    pt: index === 0 ? 1 : 0,
                    borderColor: "#aaa",
                  }}
                >
                  {!moduleData?.moduleDisabled ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          minWidth: 8 / 10,
                          maxWidth: 8 / 10,
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 16, fontWeight: 500, pr: 2 }}
                        >
                          {index + 1}.
                        </Typography>
                        {(moduleData.score / moduleData.maxScore) * 100 <
                          50 && (
                          <WarningAmberIcon
                            sx={{ color: CGI_RED, mr: 1, mb: 0.3 }}
                          />
                        )}
                        <Typography
                          sx={{ mb: 0.2, fontSize: { xs: 13, lg: 15 } }}
                        >
                          {moduleData.title}: {moduleData.score} /{" "}
                          {moduleData.maxScore} {t("points")}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        <Divider
                          flexItem
                          orientation="vertical"
                          sx={{ ml: 2, mr: 1 }}
                        />
                        <CircularProgressWithLabel
                          progressValue={
                            (moduleData.score / moduleData.maxScore) * 100
                          }
                        />
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <Typography sx={{ fontSize: 16, fontWeight: 500, pr: 2 }}>
                        {index + 1}.
                      </Typography>
                      <Typography
                        sx={{
                          mb: 0.2,
                          fontSize: { xs: 13, lg: 15 },
                          color: CGI_WARNING,
                        }}
                      >
                        {moduleData.title}: {t("notRelevant")}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )
            )}
          </Box>
        </Box>
        <Box
          sx={{
            width: { xs: 1 / 2, lg: 2 / 3 },
            display: "flex",
            flexDirection: "column",
            minHeight: 450,
          }}
        >
          <ResponsiveContainer>
            <RadarChart
              id="radarChartId"
              cx="50%"
              cy="50%"
              outerRadius="90%"
              data={Object.values(evaluationData.assessmentModules)
                .filter((x) => !x.moduleDisabled)
                .map((x, i) => ({
                  ...x,
                  index: i + 1,
                  totalRatingInPercent: x.score / x.maxScore,
                }))}
            >
              <PolarGrid />
              <PolarAngleAxis dataKey="index" />
              <PolarRadiusAxis domain={[0, 1]} />
              <Radar
                name="Data"
                dataKey="totalRatingInPercent"
                stroke={CGI_RED}
                fill={CGI_RED}
                fillOpacity={0.6}
              />
            </RadarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {Object.values(evaluationData.assessmentModules).map(
          (module, index) => (
            <Box
              key={index}
              sx={{ display: "flex", flexDirection: "column", mt: 5 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                  width: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: 17, fontWeight: 500, mr: 1 }}>
                    Module {index + 1}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 18,
                      textDecoration: "underline",
                      fontWeight: 500,
                    }}
                  >
                    {module.title}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifySelf: "center",
                    mx: 1,
                  }}
                >
                  {!module?.moduleDisabled ? (
                    <LinearProgressWithLabel
                      progressValue={(module.score / module.maxScore) * 100}
                      header={true}
                    />
                  ) : (
                    <Typography
                      sx={{ mb: 0.2, fontSize: 15, color: CGI_WARNING }}
                    >
                      {module.title}: {t("notCompletelyAnswered")}
                    </Typography>
                  )}
                </Box>
              </Box>
              {Object.values(module.questions).map((questionData, i) => (
                <Accordion
                  key={i}
                  sx={{
                    border: 1,
                    borderRadius: "20px !important",
                    borderColor: CGI_GREY,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        width: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {t("question")} {index + 1}.{i + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontSize: 13.5,
                        }}
                      >
                        {questionData.content}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifySelf: "center",
                        ml: 1,
                        alignItems: "center",
                      }}
                    >
                      <LinearProgressWithLabel
                        progressValue={
                          (questionData.score / questionData.maxScore) * 100
                        }
                        width={120}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ borderTop: 1, borderColor: "#bbb" }}>
                    {questionData.requirements &&
                      Object.values(questionData.requirements).map(
                        (element, j) => (
                          <Box key={j}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                ml: 1,
                                alignItems: "flex-end",
                              }}
                            >
                              <Typography
                                align="right"
                                sx={{
                                  mr: 1,
                                  fontWeight: 500,
                                  minWidth: 150,
                                  maxWidth: 150,
                                  color: returnColor(
                                    element.disabled,
                                    element.score
                                  ),
                                }}
                              >
                                •{" "}
                                {!element.disabled
                                  ? t(discreteDescriptions[element.score])
                                  : t("notRelevant")}
                                {":"}
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Inter",
                                  fontSize: 13,
                                }}
                              >
                                {element.text +
                                  ` (${t("weights", {
                                    weight: element.weight,
                                  })})`}
                              </Typography>
                            </Box>
                            {!element?.disabled && element.score <= 1 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  mb: 0.5,
                                  ml: 15,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                    minWidth: 140,
                                    maxWidth: 140,
                                  }}
                                >
                                  → {t("recommendation:")}
                                </Typography>
                                <Typography
                                  sx={{
                                    ml: 0.5,
                                    fontStyle: "italic",
                                    pr: 1,
                                  }}
                                >
                                  {element.solution}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )
                      )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )
        )}
      </Box>
      <Typography sx={{ mt: 12 }}>
        <SpecialText
          style={{
            fontSize: 30,
            fontStyle: "italic",
            pr: 1,
            fontWeight: 500,
            background: "-webkit-linear-gradient(45deg, #138656, #333333)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {t("recommendationForAction")}:
        </SpecialText>
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {evaluationData.recommendations &&
        evaluationData.recommendations.map((recommendation, index) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              ml: 2,
              mb: 2,
              pb: 2,
              borderBottom: 1,
              borderColor: "#ddd",
            }}
            key={index}
          >
            <Typography
              sx={{ mr: 1, mt: 0.4 }}
              style={{
                background: "-webkit-linear-gradient(45deg, #138656, #333333)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {index + 1}.
            </Typography>
            <Typography sx={{ fontSize: 17, width: 2 / 3 }}>
              {recommendation.recommendation}
            </Typography>
            <Typography
              sx={{
                ml: 2,
                fontSize: 11,
                fontFamily: "Inter",
                alignSelf: "flex-end",
                width: 1 / 3,
              }}
            >
              ({t("insufficient")}: {recommendation.requirement})
            </Typography>
          </Box>
        ))}
      {showExit && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: 1,
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{ mt: 10, borderRadius: 20, border: 3, fontSize: 16 }}
            onClick={() => exit()}
          >
            {t("exit")}
          </Button>
        </Box>
      )}
    </Box>
  );
}
